import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, type PendingDeviceConfirmation } from '../services/api'
import { showToast } from './Toast'

interface Props {
  open: boolean
  pending: PendingDeviceConfirmation[]
  /** acknowledge 完了後に呼ぶ (= gate を再評価して unmount). */
  onConfirmed: () => void
}

/**
 * 新規 device 登録の本人確認モーダル (#791 ② / #802 非破壊化).
 *
 * #792 監査の「未確認の新規 passkey 登録」がある時、RequireAuth が全画面 block
 * で強制表示する (= close/skip 不可、URL bypass 不能)。
 *
 * - 「はい、自分で登録した」→ 全 pending を acknowledge (confirmedAt mark) ← 非破壊
 * - 「心当たりがない場合」→ `/profile/passkeys` へ誘導。**破壊操作 (全 passkey 失効)
 *   は modal のワンタップに載せず**、確認 dialog のある設定画面 reset フローに集約
 *   (#802: 強制 modal の誤タップで全失効する事故を防ぐ)。RequireAuth は
 *   `/profile/passkeys` では device-confirm gate を skip するので到達可能。
 */
export default function DeviceConfirmModal({ open, pending, onConfirmed }: Props) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const fmtTime = (iso: string | null): string => {
    if (!iso) return '-'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? iso : d.toLocaleString(locale)
  }

  const handleAcknowledge = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      for (const p of pending) {
        await api.confirmDevice(p.credentialId, 'acknowledge')
      }
      showToast({ type: 'success', message: t('deviceConfirm.acknowledged') })
      onConfirmed()
    } catch (e) {
      console.error('[DeviceConfirm] acknowledge failed:', e)
      showToast({ type: 'error', message: t('deviceConfirm.error') })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100">
          <ShieldAlert size={32} className="text-amber-600" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('deviceConfirm.title')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-4 leading-relaxed">
          {t('deviceConfirm.description')}
        </p>

        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 mb-5 space-y-2">
          {pending.map((p) => (
            <div key={p.credentialId} className="text-xs font-body text-text-primary">
              <div>
                <span className="text-text-secondary">{t('deviceConfirm.deviceLabel')}: </span>
                {p.deviceLabel || '-'}
              </div>
              <div>
                <span className="text-text-secondary">{t('deviceConfirm.ipLabel')}: </span>
                {p.ipAddress || '-'}
              </div>
              <div>
                <span className="text-text-secondary">{t('deviceConfirm.timeLabel')}: </span>
                {fmtTime(p.enrolledAt)}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleAcknowledge}
          disabled={submitting}
          className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
            submitting ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-yokoso-red text-white tap-effect'
          }`}
        >
          {submitting ? '...' : t('deviceConfirm.yesAction')}
        </button>
        {/* #802: 破壊操作 (全 passkey 失効) は modal ワンタップに載せず、確認 dialog
            のある /profile/passkeys へ誘導 (誤タップ事故防止)。 */}
        <button
          onClick={() => navigate('/profile/passkeys')}
          disabled={submitting}
          className="w-full h-11 mt-2 rounded-xl font-body text-sm text-amber-700 underline tap-effect disabled:opacity-50"
        >
          {t('deviceConfirm.noAction')}
        </button>
      </div>
    </div>
  )
}
